import { forwardRef } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from './cn.js';

/**
 * Status chip.
 *
 * `danger` is for genuine errors and destruction only. An overdue rent payment is `warn`,
 * not `danger`. See docs/DESIGN_SYSTEM.md §3.
 */
const chip = cva(
  'inline-flex items-center gap-1 rounded-pill px-2.5 py-1 text-[0.75rem] font-semibold whitespace-nowrap',
  {
    variants: {
      tone: {
        neutral: 'bg-surface-sunk text-ink-muted',
        accent: 'bg-accent-soft text-accent',
        positive: 'bg-positive-soft text-positive',
        warn: 'bg-warn-soft text-warn',
        danger: 'bg-danger-soft text-danger',
        info: 'bg-info-soft text-info',
      },
    },
    defaultVariants: { tone: 'neutral' },
  },
);

export interface ChipProps
  extends React.HTMLAttributes<HTMLSpanElement>, VariantProps<typeof chip> {}

export const Chip = forwardRef<HTMLSpanElement, ChipProps>(function Chip(
  { tone, className, ...rest },
  ref,
) {
  return <span ref={ref} className={cn(chip({ tone }), className)} {...rest} />;
});

/**
 * The small rounded letter tile the reference uses on bill/payment cards.
 * Colour is chosen by the caller — never derived from the name, which would produce
 * meaningless colour that changes when someone is renamed.
 */
export function Avatar({
  letter,
  tone = 'accent',
  className,
}: {
  letter: string;
  tone?: 'neutral' | 'accent' | 'positive' | 'warn' | 'info';
  className?: string;
}) {
  const tones = {
    neutral: 'bg-surface-sunk text-ink-muted',
    accent: 'bg-accent-soft text-accent',
    positive: 'bg-positive-soft text-positive',
    warn: 'bg-warn-soft text-warn',
    info: 'bg-info-soft text-info',
  } as const;
  return (
    <span
      aria-hidden="true"
      className={cn(
        'flex size-7 shrink-0 items-center justify-center rounded-sm text-[0.8125rem] font-bold',
        tones[tone],
        className,
      )}
    >
      {letter}
    </span>
  );
}
